import { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_API_URL;

function timeAgo(unix) {
  const diff = Date.now() / 1000 - unix;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function getSentiment(headline) {
  if (!headline) return 'NEUTRAL';
  const h = headline.toLowerCase();
  const bullish = ['surge', 'soar', 'beat', 'record', 'growth', 'profit', 'upgrade',
    'outperform', 'rises', 'gains', 'strong', 'exceeds', 'jumps', 'rally', 'positive',
    'boosts', 'tops', 'higher', 'expands', 'wins', 'launches', 'milestone'];
  const bearish = ['fall', 'drop', 'miss', 'loss', 'decline', 'downgrade', 'crash',
    'weak', 'below', 'cut', 'layoff', 'warning', 'risk', 'concern', 'lower', 'slump',
    'tumble', 'plunge', 'disappoints', 'probe', 'lawsuit', 'recall', 'halts'];
  const bScore = bullish.filter(w => h.includes(w)).length;
  const eScore = bearish.filter(w => h.includes(w)).length;
  if (bScore > eScore) return 'BULLISH';
  if (eScore > bScore) return 'BEARISH';
  return 'NEUTRAL';
}

const SENTIMENT_STYLE = {
  BULLISH: { color: '#00ff88', bg: 'rgba(0,255,136,0.08)', border: 'rgba(0,255,136,0.2)' },
  BEARISH: { color: '#ef4444', bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.2)'  },
  NEUTRAL: { color: '#64748b', bg: 'rgba(100,116,139,0.08)', border: 'rgba(100,116,139,0.2)' },
};

const PAGE_SIZE = 5;

export default function NewsFeed({ ticker }) {
  const [articles, setArticles] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [visible,  setVisible]  = useState(PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setVisible(PAGE_SIZE);
    fetch(`${API_URL}/api/seo/${ticker}/news`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { if (!cancelled) { setArticles(d.articles || []); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError('News unavailable'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [ticker]);

  const shown = articles.slice(0, visible);
  const hasMore = visible < articles.length;

  return (
    <div style={{ background: '#0f0f23', border: '1px solid #1e1e3a',
      borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
      <h3 style={{ margin: '0 0 16px', color: '#e2e8f0', fontSize: 15, fontWeight: 600 }}>
        Latest News
      </h3>

      {loading && (
        <div style={{ color: '#8b8ba7', fontSize: 14, padding: '16px 0' }}>Loading news…</div>
      )}
      {!loading && error && (
        <div style={{ color: '#8b8ba7', fontSize: 14 }}>{error}</div>
      )}
      {!loading && !error && articles.length === 0 && (
        <div style={{ color: '#8b8ba7', fontSize: 14 }}>No recent news found.</div>
      )}

      {!loading && !error && shown.map((article, i) => {
        const sentiment = getSentiment(article.headline);
        const ss = SENTIMENT_STYLE[sentiment];
        return (
          <a key={article.id || i} href={article.url} target="_blank" rel="noopener noreferrer"
            style={{ display: 'block', textDecoration: 'none', padding: '14px 0',
              borderBottom: i < shown.length - 1 ? '1px solid #1e1e3a' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              {article.image && (
                <img src={article.image} alt=""
                  onError={e => { e.target.style.display = 'none'; }}
                  style={{ width: 72, height: 54, objectFit: 'cover',
                    borderRadius: 6, flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Headline */}
                <div style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 500,
                  lineHeight: 1.4, marginBottom: 4,
                  overflow: 'hidden', display: '-webkit-box',
                  WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {article.headline}
                </div>
                {/* Excerpt */}
                {article.summary && (
                  <div style={{ color: '#8b8ba7', fontSize: 12, lineHeight: 1.5,
                    marginBottom: 6, overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {article.summary}
                  </div>
                )}
                {/* Meta row */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {article.source}
                  </span>
                  <span style={{ fontSize: 11, color: '#8b8ba7' }}>
                    · {timeAgo(article.datetime)}
                  </span>
                  {/* Sentiment pill */}
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '0.15rem 0.5rem',
                    borderRadius: '20px', background: ss.bg,
                    border: `1px solid ${ss.border}`, color: ss.color,
                    letterSpacing: '0.05em' }}>
                    {sentiment}
                  </span>
                  {/* NEWS-MULTI-SOURCE: source badge — degrades to nothing if
                      sourceType is absent (e.g. cached responses from before
                      this change). google_news_rss gets a distinct dimmer
                      style since that source is confirmed stale (median ~6.6
                      day old articles), never presented as equally fresh. */}
                  {article.sourceType && (
                    <span style={{ fontSize: 9, fontWeight: 600, padding: '0.12rem 0.4rem',
                      borderRadius: '20px',
                      background: article.sourceType === 'google_news_rss' ? 'rgba(100,116,139,0.06)' : 'rgba(99,102,241,0.08)',
                      color: article.sourceType === 'google_news_rss' ? '#475569' : '#8b8ba7',
                      letterSpacing: '0.03em' }}>
                      {{
                        finnhub: 'Finnhub',
                        seeking_alpha: 'Seeking Alpha',
                        pr_newswire: 'PR Newswire',
                        globe_newswire: 'GlobeNewswire',
                        google_news_rss: 'Google News',
                      }[article.sourceType] || article.sourceType}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6366f1' }}>
                    Read →
                  </span>
                </div>
              </div>
            </div>
          </a>
        );
      })}

      {/* Load more */}
      {!loading && !error && hasMore && (
        <button onClick={() => setVisible(v => v + PAGE_SIZE)}
          style={{ width: '100%', marginTop: 12, padding: '0.6rem',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8,
            color: '#64748b', fontSize: 13, cursor: 'pointer',
            fontFamily: 'inherit', fontWeight: 600, transition: 'color 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#94a3b8'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; }}>
          Load more ({articles.length - visible} remaining)
        </button>
      )}
    </div>
  );
}
